/**
 * Proyecto: MOGOTAX-CONDUCTOR
 * Autor: Megatrack GPS SAS
 * Fecha de creación: 05 de Diciembre 2025
 * Licencia: Propiedad Privada
 *
 * Este código es propiedad exclusiva de Megatrack GPS SAS & Mogotax SAS
 *
 * Copyright (c) 2025 Megatrack GPS SAS. Todos los derechos reservados.
 */

import React, { useState, useEffect, useRef } from "react";
import { Modal, Input, Button, Progress, Alert, message } from "antd";
import { ClockCircleOutlined, EnvironmentOutlined, WarningOutlined } from "@ant-design/icons";
import { calculateDistance } from "../../../utils/distance";

const { TextArea } = Input;

const TIEMPO_ESPERA_SEGUNDOS = 900; // 15 minutos
const RADIO_MAXIMO_METROS = 100; // Radio de 100 metros

/**
 * Modal para marcar traslado como Fallido por Validar
 * Requiere que el conductor permanezca en un radio de 100m durante 15 minutos
 */
const FallidoValidacionModal = ({ visible, onClose, onConfirm, currentLocation }) => {
  const [motivo, setMotivo] = useState("");
  const [tiempoRestante, setTiempoRestante] = useState(TIEMPO_ESPERA_SEGUNDOS);
  const [ubicacionInicial, setUbicacionInicial] = useState(null);
  const [fueraDeRadio, setFueraDeRadio] = useState(false);
  const [tiempoFueraDeRadio, setTiempoFueraDeRadio] = useState(0);
  const [confirmando, setConfirmando] = useState(false);

  const intervaloRef = useRef(null);
  const intervaloUbicacionRef = useRef(null);

  // Formatear tiempo en mm:ss
  const formatearTiempo = (segundos) => {
    const minutos = Math.floor(segundos / 60);
    const segs = segundos % 60;
    return `${minutos.toString().padStart(2, "0")}:${segs.toString().padStart(2, "0")}`;
  };

  // Calcular progreso en porcentaje
  const progreso = ((TIEMPO_ESPERA_SEGUNDOS - tiempoRestante) / TIEMPO_ESPERA_SEGUNDOS) * 100;

  // Inicializar ubicación inicial cuando se abre el modal
  useEffect(() => {
    if (visible && currentLocation && !ubicacionInicial) {
      setUbicacionInicial({
        latitude: currentLocation.latitude,
        longitude: currentLocation.longitude,
      });
    }
  }, [visible, currentLocation, ubicacionInicial]);

  // Cronómetro de cuenta regresiva
  useEffect(() => {
    if (visible && ubicacionInicial) {
      intervaloRef.current = setInterval(() => {
        setTiempoRestante((prev) => {
          if (prev <= 1) {
            clearInterval(intervaloRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => {
        if (intervaloRef.current) {
          clearInterval(intervaloRef.current);
        }
      };
    }
  }, [visible, ubicacionInicial]);

  // Validación de ubicación cada 5 segundos
  useEffect(() => {
    if (visible && ubicacionInicial && currentLocation) {
      intervaloUbicacionRef.current = setInterval(() => {
        const distancia = calculateDistance(
          currentLocation.latitude,
          currentLocation.longitude,
          ubicacionInicial.latitude,
          ubicacionInicial.longitude
        );

        if (distancia > RADIO_MAXIMO_METROS) {
          setFueraDeRadio(true);
          setTiempoFueraDeRadio((prev) => prev + 5);
        } else {
          if (fueraDeRadio) {
            setFueraDeRadio(false);
          }
        }
      }, 5000); // Verificar cada 5 segundos

      return () => {
        if (intervaloUbicacionRef.current) {
          clearInterval(intervaloUbicacionRef.current);
        }
      };
    }
  }, [visible, ubicacionInicial, currentLocation, fueraDeRadio]);

  // Limpiar cuando se cierra el modal
  useEffect(() => {
    if (!visible) {
      setTiempoRestante(TIEMPO_ESPERA_SEGUNDOS);
      setUbicacionInicial(null);
      setMotivo("");
      setFueraDeRadio(false);
      setTiempoFueraDeRadio(0);
      if (intervaloRef.current) {
        clearInterval(intervaloRef.current);
      }
      if (intervaloUbicacionRef.current) {
        clearInterval(intervaloUbicacionRef.current);
      }
    }
  }, [visible]);

  const handleConfirmar = async () => {
    if (!motivo.trim()) {
      message.error("Por favor, ingrese el motivo del fallido");
      return;
    }

    if (tiempoRestante > 0) {
      message.warning(`Debe esperar ${formatearTiempo(tiempoRestante)} más`);
      return;
    }

    if (fueraDeRadio) {
      message.error("No puede confirmar mientras está fuera del radio permitido");
      return;
    }

    setConfirmando(true);
    try {
      await onConfirm({
        motivo,
        ubicacionInicial,
        tiempoEspera: TIEMPO_ESPERA_SEGUNDOS,
      });
    } finally {
      setConfirmando(false);
    }
  };

  const puedeConfirmar = tiempoRestante === 0 && !fueraDeRadio && motivo.trim().length > 0;

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <WarningOutlined style={{ color: "#ff4d4f", fontSize: 24 }} />
          <span>Marcar Traslado como Fallido</span>
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="cancelar" onClick={onClose} disabled={confirmando}>
          Cancelar
        </Button>,
        <Button
          key="confirmar"
          type="primary"
          danger
          onClick={handleConfirmar}
          disabled={!puedeConfirmar}
          loading={confirmando}
        >
          Confirmar Fallido
        </Button>,
      ]}
      closable={!confirmando}
      maskClosable={false}
      width={500}
    >
      <div style={{ padding: "20px 0" }}>
        {/* Cronómetro */}
        <div style={{ marginBottom: 24, textAlign: "center" }}>
          <ClockCircleOutlined style={{ fontSize: 48, color: tiempoRestante > 0 ? "#fa7114" : "#52c41a" }} />
          <div style={{ fontSize: 32, fontWeight: "bold", marginTop: 8 }}>
            {formatearTiempo(tiempoRestante)}
          </div>
          <div style={{ fontSize: 14, color: "#8c8c8c", marginTop: 4 }}>
            {tiempoRestante > 0 ? "Tiempo restante de espera" : "Tiempo de espera completado"}
          </div>
          <Progress
            percent={progreso}
            status={tiempoRestante > 0 ? "active" : "success"}
            strokeColor={tiempoRestante > 0 ? "#fa7114" : "#52c41a"}
            style={{ marginTop: 16 }}
          />
        </div>

        {/* Alertas */}
        {fueraDeRadio && (
          <Alert
            message="¡Advertencia! Está fuera del área permitida"
            description={`Debe permanecer en un radio de ${RADIO_MAXIMO_METROS}m alrededor del punto inicial. Regrese al área para poder confirmar.`}
            type="error"
            icon={<EnvironmentOutlined />}
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {tiempoFueraDeRadio > 0 && !fueraDeRadio && (
          <Alert
            message="Advertencia"
            description={`Ha estado fuera del área durante ${tiempoFueraDeRadio} segundos. Permanezca en el punto.`}
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {tiempoRestante === 0 && !fueraDeRadio && (
          <Alert
            message="Tiempo completado"
            description="Ha esperado los 15 minutos requeridos. Puede proceder a marcar el traslado como fallido."
            type="success"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {/* Instrucciones */}
        <Alert
          message="Instrucciones"
          description={
            <ul style={{ paddingLeft: 20, margin: 0 }}>
              <li>Debe permanecer en un radio de 100 metros del punto actual</li>
              <li>Debe esperar 15 minutos antes de poder confirmar</li>
              <li>No se mueva del sitio durante este tiempo</li>
              <li><strong style={{ color: "#fa7114" }}>⚠️ PRECAUCIÓN:</strong> Si se sale de la app, el cronómetro se detendrá</li>
              <li><strong style={{ color: "#fa7114" }}>⚠️ PRECAUCIÓN:</strong> Si cierra la aplicación, el cronómetro se reiniciará</li>
            </ul>
          }
          type="info"
          style={{ marginBottom: 16 }}
        />

        {/* Motivo */}
        <div>
          <label style={{ display: "block", marginBottom: 8, fontWeight: "bold" }}>
            Motivo del fallido <span style={{ color: "red" }}>*</span>
          </label>
          <TextArea
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Explique detalladamente el motivo por el cual el traslado no pudo completarse"
            rows={4}
            maxLength={500}
            showCount
            disabled={confirmando}
          />
        </div>
      </div>
    </Modal>
  );
};

export default FallidoValidacionModal;
